import type { ModemSignal } from "../lib/modemSignals";

const GREEN = "#1eaa6f";
const AMBER = "#f4b740";
const RED = "#e35b5b";
const OFF = "rgba(148,163,184,0.3)";

function level(m: Partial<ModemSignal>): { bars: number; color: string } {
  const { rsrp, sinr, csq } = m;
  if (typeof rsrp === "number") {
    if (rsrp >= -85) return { bars: 4, color: GREEN };
    if (rsrp >= -95) return { bars: 3, color: GREEN };
    if (rsrp >= -105) return { bars: 2, color: AMBER };
    return { bars: 1, color: RED };
  }
  if (typeof sinr === "number") {
    if (sinr >= 20) return { bars: 4, color: GREEN };
    if (sinr >= 13) return { bars: 3, color: GREEN };
    if (sinr >= 5) return { bars: 2, color: AMBER };
    return { bars: 1, color: RED };
  }
  if (typeof csq === "number" && csq !== 99) {
    if (csq >= 20) return { bars: 4, color: GREEN };
    if (csq >= 14) return { bars: 3, color: GREEN };
    if (csq >= 8) return { bars: 2, color: AMBER };
    return { bars: 1, color: RED };
  }
  return { bars: 0, color: OFF };
}

// Compact LTE signal bars for a dyno tile. The node's RF detail lives in the
// tooltip. Renders nothing for a non-cellular / unknown node.
export default function SignalBars({ modem }: { modem?: ModemSignal | null }) {
  if (!modem) return null;
  const { bars, color } = level(modem);
  const parts: string[] = [];
  if (modem.operator) parts.push(modem.operator);
  if (typeof modem.rsrp === "number") parts.push(`RSRP ${modem.rsrp} dBm`);
  if (typeof modem.sinr === "number") parts.push(`SINR ${modem.sinr} dB`);
  if (modem.band) parts.push(modem.band);
  const title = `LTE signal${parts.length ? " · " + parts.join(" · ") : ""}`;
  const heights = [4, 6, 8, 10];
  return (
    <span
      title={title}
      aria-label={title}
      style={{ display: "inline-flex", alignItems: "flex-end", gap: 1, height: 10 }}
    >
      {heights.map((h, i) => (
        <span
          key={i}
          style={{
            width: 2,
            height: h,
            borderRadius: 1,
            background: i < bars ? color : OFF,
          }}
        />
      ))}
    </span>
  );
}
